<script>
import { noop } from '@/utils/utils';
import { isArray, isBoolean, isEmpty, isFunction, isNumber } from '@/utils/is';

const defaultConfig = {
  span: 24,
  gutter: 20,
  size: 'medium',
  labelSuffix: '',
  options: [],
  operation: {}
};

export default {
  props: {
    showClearable: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    config: {
      type: Object,
      default: () => defaultConfig
    },
    model: {
      type: Object,
      default: () => ({})
    },
    dataSource: {
      type: Object,
      default: () => ({})
    },
    rules: {
      type: Object,
      default: () => ({})
    },
    i18nPath: {
      type: String,
      default: ''
    }
  },
  computed: {
    commonConfig() {
      return Object.assign({}, defaultConfig, this.config);
    }
  },
  methods: {
    translateTo2(text, i18nPath) {
      i18nPath ||= this.i18nPath;

      if (i18nPath === 'part') {
        return this.translateTo(text, 'form');
      } else if (i18nPath) {
        return this.$t(text);
      }

      return text;
    },
    resetFields() {
      this.$refs.form.resetFields();
    },
    validate(...rest) {
      return this.$refs.form.validate(...rest);
    },
    clearValidate() {
      this.$refs.form.clearValidate();
    },
    useFormItem(config, children) {
      const {
        span = this.commonConfig.span,
        colProps = {},
        offset,
        prop,
        label,
        rules,
        labelWidth,
        i18nPath
      } = config;

      const labelText = this.translateTo2(label, i18nPath);

      return (
        <el-col
          span={span}
          offset={offset}
          props={colProps}
          key={prop}
          type="flex"
          style="flex-wrap: wrap;"
        >
          <el-form-item
            style="display:flex;"
            label={labelText}
            prop={prop}
            label-width={labelWidth}
            rules={rules}
          >
            {children}
          </el-form-item>
        </el-col>
      );
    },
    useText(config) {
      const { attrs = {}, showFormat, prop, defaultValue } = config;

      const value = this.model[prop] || defaultValue;
      const showValue = isFunction(showFormat) ? showFormat(value) : value;

      const text = <span {...attrs}>{showValue}</span>;

      return text;
    },
    useImgUpload(config) {
      const { attrs = {}, disabled = false, showFormat, prop } = config;

      const value = this.model[prop];
      const showValue = isFunction(showFormat) ? showFormat(value) : value;

      function handleRemove(file) {
        console.log(file);
      }
      function handlePictureCardPreview(_file) {
        // this.dialogImageUrl = file.url;
        // this.dialogVisible = true;
      }
      function handleDownload(file) {
        console.log(file);
      }

      const showHandler = file => {
        if (disabled) return null;
        return [
          <span class="el-upload-list__item-delete" onClick={() => handleDownload(file)}>
            <i class="el-icon-download"></i>
          </span>,
          <span class="el-upload-list__item-delete" onClick={() => handleRemove(file)}>
            <i class="el-icon-delete"></i>
          </span>
        ];
      };

      const imgUpload = (
        <el-upload
          {...attrs}
          action={attrs.action}
          multiple={attrs.multiple}
          list-type={attrs.listType}
          headers={attrs.headers}
          name={attrs.name}
          limit={attrs.limit}
          http-request={attrs.httpRequest}
          file-list={showValue}
          scopedSlots={{
            file: file => {
              return (
                <div>
                  <img class="el-upload-list__item-thumbnail" src={file.url} alt="" />
                  <span class="el-upload-list__item-actions">
                    <span
                      class="el-upload-list__item-preview"
                      onClick={() => handlePictureCardPreview(file)}
                    >
                      <i class="el-icon-zoom-in"></i>
                    </span>
                    {showHandler(file)}
                  </span>
                </div>
              );
            }
          }}
        >
          {showValue.length >= 1 ? null : <i class="el-icon-plus"></i>}
        </el-upload>
      );
      return imgUpload;
    },
    useInput(config) {
      let { attrs = {}, disabled = false, showFormat, placeholder, prop, i18nPath, slots } = config;

      const type = attrs.type || 'text';

      const isDisabled = isFunction(disabled) ? disabled() : disabled;

      const value = this.model[prop];
      const showValue = isFunction(showFormat) ? showFormat(value) : value;

      const handleInput = e => (this.model[config.prop] = e);

      const append = isFunction(slots?.append) ? <div slot="append">{slots.append()}</div> : null;

      const input = (
        <el-input
          props={{ ...attrs }}
          type={type}
          disabled={isDisabled}
          value={showValue}
          onInput={handleInput}
          placeholder={this.translateTo2(placeholder, i18nPath)}
        >
          {append}
        </el-input>
      );
      return input;
    },
    useSelect(config) {
      let {
        attrs = {},
        disabled,
        dataKey,
        prop,
        i18nPath,
        labelNeedTranslation,
        placeholder,
        multiple
      } = config;

      placeholder = this.translateTo2(placeholder, i18nPath);

      let _disabled = false;

      if (!isEmpty(disabled)) {
        if (isNumber(disabled)) _disabled = Boolean(disabled);
        else if (isFunction(disabled)) _disabled = disabled();
        else if (isBoolean(disabled)) _disabled = disabled;
      }

      const data = this.dataSource[dataKey];

      const options =
        isArray(data) &&
        data.map(e => {
          const label = labelNeedTranslation ? this.translateTo2(e.label, i18nPath) : e.label;

          return <el-option key={e.value} label={label} value={e.value}></el-option>;
        });

      const onChange = e => {
        if (multiple) {
          this.model[prop].splice(0, Infinity, ...e);
        } else {
          this.model[prop] = e;
        }
      };

      const select = (
        <el-select
          clearable={this.showClearable}
          disabled={_disabled}
          props={{ ...attrs }}
          multiple={multiple}
          value={this.model[prop]}
          placeholder={placeholder}
          onChange={onChange}
        >
          {options}
        </el-select>
      );
      return select;
    },
    useCheckbox(config) {
      const attrs = config.attrs || {};

      const { label, ...props } = config.componentProps || {};

      const checkbox = (
        <el-checkbox
          {...attrs}
          {...props}
          v-model={this.model[config.prop]}
          // onChange={e => (this.model[config.prop] = e)}
        >
          {label}
        </el-checkbox>
      );

      return checkbox;
    },
    useCheckboxGroup(config) {
      const { i18nPath, dataKey, prop, attrs = {} } = config;

      const data = this.dataSource[dataKey];

      const handleChange = (v, flag) => {
        if (flag) {
          this.model[prop].push(v);
        } else {
          const index = this.model[prop].findIndex(e => e === v);
          this.model[prop].splice(index, 1);
        }
      };

      const value = this.model[prop];

      const group = (
        <el-checkbox-group props={{ value, ...attrs }}>
          {data.map(opt => (
            <el-checkbox
              onChange={e => handleChange(opt.value, e)}
              label={opt.value}
              key={opt.value}
            >
              {this.translateTo2(opt.label, i18nPath)}
            </el-checkbox>
          ))}
        </el-checkbox-group>
      );

      return group;
    },
    useRadioGroup(config) {
      const { attrs = {}, disabled = false, showFormat, prop, i18nPath } = config;

      const value = this.model[prop];
      const showValue = isFunction(showFormat) ? showFormat(value) : value;

      const data = this.dataSource[config.dataKey];

      const handleChange = e => {
        this.model[prop] = e;
      };

      return (
        <el-radio-group props={{ value: showValue, disabled, ...attrs }}>
          {data.map(opt => (
            <el-radio onChange={() => handleChange(opt.value)} label={opt.value} key={opt.value}>
              {this.translateTo2(opt.label, i18nPath)}
            </el-radio>
          ))}
        </el-radio-group>
      );
    },
    useSwitch(config) {
      const { attrs = {}, disabled = false, showFormat, prop, i18nPath } = config;

      const value = this.model[prop];
      const showValue = isFunction(showFormat) ? showFormat(value) : value;

      const handleChange = e => {
        this.model[prop] = e;
      };

      let { activeText, inactiveText } = attrs;

      activeText = this.translateTo2(activeText, i18nPath);
      inactiveText = this.translateTo2(inactiveText, i18nPath);

      return (
        <el-switch
          props={{ ...attrs, activeText, inactiveText, value: showValue }}
          disabled={disabled}
          onChange={handleChange}
          // active-color="#13ce66"
          // inactive-color="#ff4949"
          // active-text="按月付费"
          // inactive-text="按年付费"
        ></el-switch>
      );
    },
    useDateTimePicker(config) {
      const { model } = this;
      const { attrs = {}, placeholder, disabled = false, prop } = config;

      const onChange = e => {
        this.model[prop] = e;
      };

      return (
        <el-date-picker
          props={{ ...attrs }}
          v-model={model[prop]}
          placeholder={placeholder}
          disabled={disabled}
          onChange={onChange}
        ></el-date-picker>
      );
    },
    useCustom(config) {
      const data = this.dataSource[config.dataKey];
      const model = this.model[config.prop];

      const component = this.$scopedSlots[config.slot];
      return component({ data, model, dataSource: this.dataSource, config });
    },
    useButton(config) {
      const { model } = this;
      const {
        handler = noop,
        type = 'default',
        loading,
        text,
        i18nPath,
        disabled,
        ...attrs
      } = config;

      const params = {
        data: model,
        setLoading: state => (config.loading = state),
        getFormRef: () => this.$refs.form
      };

      let _disabled = false;

      if (!isEmpty(disabled)) {
        if (isNumber(disabled)) _disabled = Boolean(disabled);
        else if (isFunction(disabled)) _disabled = disabled();
        else if (isBoolean(disabled)) _disabled = disabled;
      }

      const _text = isFunction(text) ? text() : text;

      const button = (
        <el-button
          props={{ ...attrs }}
          loading={loading}
          type={type}
          disabled={_disabled}
          onClick={() => handler(params)}
        >
          {this.translateTo2(_text, i18nPath)}
        </el-button>
      );

      return button;
    },
    useOperation(config) {
      if (isEmpty(config)) return null;
      const btns =
        config.buttons &&
        config.buttons
          .filter(e => {
            if (isFunction(e.show)) {
              return e.show();
            }

            return true;
          })
          .map(e => this.useButton(e));

      const btnWrap = (
        <div
          {...(config.buttonWrapProps || {})}
          class="btn-wrap"
          style={{ justifyContent: config.justifyContent }}
        >
          {btns}
        </div>
      );

      return this.useFormItem({ ...config, prop: 'form-operation' }, btnWrap);
    },
    genVNode(key, config) {
      if (!config) {
        console.log('[error][genVNode]--', '未找到配置信息');
        return null;
      }

      const dict = {
        text: this.useText,
        input: this.useInput,
        select: this.useSelect,
        checkbox: this.useCheckbox,
        checkboxGroup: this.useCheckboxGroup,
        radioGroup: this.useRadioGroup,
        imgUpload: this.useImgUpload,
        switch: this.useSwitch,
        custom: this.useCustom,
        dateTimePicker: this.useDateTimePicker
      };

      let el = null;

      if (isArray(config.children)) {
        el = config.children.map(e => dict[e.type].bind(this)(e));
      } else {
        if (!key) {
          console.log('[warn][components/form]--', `genVNode未找到key`, config);
        } else {
          el = dict[key].bind(this)(config);
        }
      }

      return this.useFormItem(config, el);
    },
    handleOptions(options, model) {
      const { genVNode } = this;

      const nodes = options
        .filter(e => {
          if (isEmpty(e.show)) return true;

          if (isBoolean(e.show)) return e.show;

          if (isFunction(e.show)) return e.show(model[e.prop], model, e);

          return true;
        })
        .map(e => genVNode(e.type, e));

      return nodes;
    }
  },

  render(_) {
    const { model, rules, inline, handleOptions } = this;

    const { options, gutter, operation, labelWidth, justify, type, size, labelSuffix } =
      this.commonConfig;

    const nodes = handleOptions(options, model);

    return (
      <div class="custom-form-container">
        <el-form
          ref="form"
          inline={inline}
          props={{ model }}
          rules={rules}
          size={size}
          label-suffix={labelSuffix}
          label-width={labelWidth}
        >
          <el-row gutter={gutter} justify={justify} type={type}>
            {nodes}
            {this.useOperation(operation)}
          </el-row>
        </el-form>
      </div>
    );
  }
};
</script>

<style lang="scss">
.custom-form-container {
  width: 100%;

  .el-form-item {
    // display: flex;

    .el-form-item__label {
      line-height: 30px;
    }

    .el-form-item__content {
      flex: 1;
      margin-left: unset !important;
      line-height: 30px;
      // display: flex;
      // align-items: center;
    }

    .el-date-editor {
      width: auto;
    }

    .el-radio-group {
      height: 100%;

      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 12px !important;

      .el-radio {
        line-height: 30px;
      }
    }
  }

  .el-input__inner::placeholder {
    color: #02757e;
  }

  .el-select {
    width: 100%;
  }

  .btn-wrap {
    display: flex;
    flex-wrap: wrap;
  }
}
</style>
